import { useEffect, useState, type FormEvent } from "react";
import { Check, Loader2, RefreshCw, X } from "lucide-react";
import { Modal } from "@veorest/ui";
import { useI18n } from "../../i18n";
import { usePartnerSessionStore } from "../../lib/partnerAuth";
import {
  checkAccountUsername,
  checkStoreSlug,
  generatePassword,
  handleErrorKey,
  type HandleCheck,
} from "../../lib/partnerAccounts";

export type ClientCreateMode = "passcode" | "ready";

export interface ClientCreateValues {
  /** Which account-creation rail the Partner chose. */
  mode: ClientCreateMode;
  name: string;
  phone: string;
  address: string;
  /** Reserved client login username (required). */
  username: string;
  /** Optional contact email — invoices later. */
  email?: string;
  /** Ready-to-Use only: chosen public store slug. */
  slug: string;
  /** Ready-to-Use only: initial password (shown once by the caller). */
  password: string;
}

interface ClientCreateModalProps {
  open: boolean;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (values: ClientCreateValues) => Promise<void>;
}

const MIN_PASSWORD = 8;
const CHECK_DEBOUNCE_MS = 450;

export function ClientCreateModal({
  open,
  busy,
  onClose,
  onSubmit,
}: ClientCreateModalProps) {
  const { t } = useI18n();
  const partnerId = usePartnerSessionStore((s) => s.session?.partnerId ?? null);
  const [mode, setMode] = useState<ClientCreateMode>("passcode");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [slug, setSlug] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [slugCheck, setSlugCheck] = useState<HandleCheck | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [usernameCheck, setUsernameCheck] = useState<HandleCheck | null>(null);
  const [usernameChecking, setUsernameChecking] = useState(false);

  const resetForm = () => {
    setMode("passcode");
    setName("");
    setPhone("");
    setAddress("");
    setUsername("");
    setEmail("");
    setSlug("");
    setPassword("");
    setFormError("");
    setSlugCheck(null);
    setUsernameCheck(null);
  };

  // Live slug availability (server-enforced; this is only feedback).
  useEffect(() => {
    if (mode !== "ready" || !partnerId) {
      setSlugCheck(null);
      return;
    }
    const value = slug.trim().toLowerCase();
    if (value.length === 0) {
      setSlugCheck(null);
      return;
    }
    setSlugChecking(true);
    const timer = window.setTimeout(() => {
      checkStoreSlug(partnerId, value)
        .then((res) => setSlugCheck(res))
        .catch(() => setSlugCheck(null))
        .finally(() => setSlugChecking(false));
    }, CHECK_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [mode, partnerId, slug]);

  // Live username availability for both modes (drivers use the same namespace).
  useEffect(() => {
    if (!partnerId) {
      setUsernameCheck(null);
      return;
    }
    const value = username.trim().toLowerCase();
    if (value.length < 3) {
      setUsernameCheck(null);
      return;
    }
    setUsernameChecking(true);
    const timer = window.setTimeout(() => {
      checkAccountUsername(partnerId, value, "store")
        .then((res) => setUsernameCheck(res))
        .catch(() => setUsernameCheck(null))
        .finally(() => setUsernameChecking(false));
    }, CHECK_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [partnerId, username]);

  const renderHandleStatus = (
    checking: boolean,
    check: HandleCheck | null
  ) => {
    if (checking) {
      return (
        <span className="inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t("accounts.checking")}
        </span>
      );
    }
    if (!check) return null;
    if (check.available) {
      return (
        <span className="inline-flex items-center gap-1 text-[var(--accent)]">
          <Check className="h-3 w-3" />
          {t("accounts.available")}
        </span>
      );
    }
    return (
      <span className="text-[var(--danger)]">{t(handleErrorKey(check.error))}</span>
    );
  };

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (mode === "ready") {
      if (password.length < MIN_PASSWORD) {
        setFormError(t("accounts.passwordMin"));
        return;
      }
      if (slugCheck && !slugCheck.available) {
        setFormError(t(handleErrorKey(slugCheck.error)));
        return;
      }
      if (usernameCheck && !usernameCheck.available) {
        setFormError(t(handleErrorKey(usernameCheck.error)));
        return;
      }
    }
    setFormError("");

    await onSubmit({
      mode,
      name,
      phone,
      address,
      username: username.trim().toLowerCase(),
      email: email.trim() || undefined,
      slug: mode === "ready" ? slug.trim().toLowerCase() : "",
      password: mode === "ready" ? password : "",
    });
    resetForm();
  };


  return (
    <Modal open={open} onClose={onClose} panelClassName="max-w-md">
      <div className="flex max-h-[calc(100vh-32px)] flex-col overflow-y-auto">
        <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-5 py-4">
          <h2
            id="create-client-title"
            className="text-lg font-extrabold text-[var(--text-primary)]"
          >
            {t("clients.createTitle")}
          </h2>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--border-subtle)]"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 px-5 py-4">
          <div
            className="grid grid-cols-2 gap-1 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-1"
            role="radiogroup"
            aria-label={t("clients.createTitle")}
          >
            {(
              [
                { key: "passcode" as const, label: t("clients.modePasscode") },
                { key: "ready" as const, label: t("clients.modeReady") },
              ]
            ).map((option) => {
              const active = mode === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => {
                    setMode(option.key);
                    setFormError("");
                  }}
                  className={`min-h-[42px] rounded-lg px-3 text-xs font-black transition ${
                    active
                      ? "bg-[var(--surface)] text-[var(--accent)] shadow-sm"
                      : "text-[var(--text-secondary)]"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] leading-5 text-[var(--text-muted)]">
            {mode === "ready" ? t("clients.readyHint") : t("clients.passcodeHint")}
          </p>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clients.businessName")}
            </span>
            <input
              className="input-base w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              autoFocus
              placeholder={t("clients.businessName")}
            />
          </label>

          {mode === "ready" ? (
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                {t("clients.slug")}
              </span>
              <input
                className="input-base w-full"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
                minLength={3}
                maxLength={60}
                autoCapitalize="none"
                spellCheck={false}
                dir="ltr"
                placeholder="alnoor-pharmacy"
              />
              <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--text-muted)]">
                {t("clients.slugHint")}
                {renderHandleStatus(slugChecking, slugCheck)}
              </span>
            </label>
          ) : null}

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clients.phone")}
            </span>
            <input
              className="input-base w-full"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              placeholder="+964…"
              inputMode="tel"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clients.address")}
            </span>
            <input
              className="input-base w-full"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
              placeholder={t("clients.addressPlaceholder")}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clients.username")}
            </span>
            <input
              className="input-base w-full"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              maxLength={30}
              autoCapitalize="none"
              spellCheck={false}
              dir="ltr"
              placeholder="e.g. alnoor-pharmacy"
            />
            <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--text-muted)]">
              {t("clients.usernameHint")}
              {renderHandleStatus(usernameChecking, usernameCheck)}
            </span>
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clients.emailOptional")}
            </span>
            <input
              className="input-base w-full"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          {mode === "ready" ? (
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                {t("accounts.initialPassword")}
              </span>
              <div className="flex items-center gap-2">
                <input
                  className="input-base w-full"
                  type="text"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setFormError("");
                  }}
                  required
                  minLength={MIN_PASSWORD}
                  autoComplete="new-password"
                  spellCheck={false}
                  dir="ltr"
                  placeholder={t("accounts.passwordPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => setPassword(generatePassword())}
                  className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl border border-[var(--border-subtle)] px-3 text-xs font-bold text-[var(--accent)]"
                  title={t("accounts.generate")}
                >
                  <RefreshCw className="h-4 w-4" />
                  {t("accounts.generate")}
                </button>
              </div>
              <span className="text-[11px] text-[var(--text-muted)]">
                {t("accounts.passwordMin")}
              </span>
            </label>
          ) : null}

          {formError ? (
            <p className="text-xs font-bold text-[var(--danger)]">{formError}</p>
          ) : null}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={onClose}
              disabled={busy}
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={busy}>
              {busy
                ? t("clients.creating")
                : mode === "ready"
                  ? t("clients.createReady")
                  : t("clients.create")}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
